import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import StatsCard from "@/components/dashboard/StatsCard";
import BarChart from "@/components/dashboard/BarChart";
import {
  LineChart,
  Line,
  BarChart as RechartsBarChart,
  Bar,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  TrendingUp,
  Users,
  DollarSign,
  Target,
  AlertCircle,
  Calendar,
  Clock,
} from "lucide-react";
import {
  fetchOverviewAnalytics,
  OverviewAnalytics,
  DateRange,
  fetchRevenueAnalytics,
  fetchSubscriptionAnalytics,
  fetchMonthlyGrowthTrends,
} from "@/api/business-analytics";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Tooltip as UiTooltip,
  TooltipContent as UiTooltipContent,
  TooltipProvider as UiTooltipProvider,
  TooltipTrigger as UiTooltipTrigger,
} from "@/components/ui/tooltip";

interface OverviewTabProps {
  dateRange?: DateRange;
}

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

// Helper functions to create time-based date ranges
const getTodaySoFar = (): DateRange => {
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  return { from: startOfToday, to: now };
};

const getYesterday = (): DateRange => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const startOfYesterday = new Date(
    yesterday.getFullYear(),
    yesterday.getMonth(),
    yesterday.getDate()
  );
  const endOfYesterday = new Date(
    yesterday.getFullYear(),
    yesterday.getMonth(),
    yesterday.getDate(),
    23,
    59,
    59
  );
  return { from: startOfYesterday, to: endOfYesterday };
};

const getThisMonth = (): DateRange => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: startOfMonth, to: endOfMonth };
};

const getLastMonth = (): DateRange => {
  const now = new Date();
  const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
  return { from: startOfLastMonth, to: endOfLastMonth };
};

// Additional comparison ranges
const getDayBeforeYesterday = (): DateRange => {
  const d = new Date();
  d.setDate(d.getDate() - 2);
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
  return { from: start, to: end };
};

const getMonthBeforeLast = (): DateRange => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 2, 1);
  const end = new Date(now.getFullYear(), now.getMonth() - 1, 0);
  return { from: start, to: end };
};

// Custom tooltip component that supports dark mode
interface TooltipPayload {
  color: string;
  name: string;
  value: string | number;
  // Original data point for the hovered x-axis category
  payload?: Record<string, unknown>;
}

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}) => {
  if (active && payload && payload.length) {
    const p0 = (
      payload as Array<TooltipPayload & { payload?: Record<string, unknown> }>
    )[0];
    const dataPoint = p0 && p0.payload ? p0.payload : {};
    const newSubsRaw = (dataPoint as Record<string, unknown>)[
      "newSubscriptions"
    ];
    const newSubs: number =
      typeof newSubsRaw === "number" ? newSubsRaw : Number(newSubsRaw ?? 0);
    return (
      <div className="bg-background border border-border rounded-lg shadow-lg p-3">
        <p className="font-medium text-foreground">{label}</p>
        {payload.map((entry: TooltipPayload, index: number) => {
          const isTotalSubs =
            typeof entry.name === "string" &&
            entry.name === "Total Subscriptions";
          const value = entry.value;
          return (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {isTotalSubs
                ? `${entry.name}: ${value} (+${newSubs})`
                : `${entry.name}: ${value}`}
            </p>
          );
        })}
      </div>
    );
  }
  return null;
};

// Tooltip specifically for Plan Performance pie to show combined + INR/USD splits
const PlanPieTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{
    color: string;
    name: string;
    value: number;
    payload?: {
      subscriptions?: number;
      inrRevenue?: number;
      usdRevenue?: number;
      name?: string;
    };
  }>;
}) => {
  if (!active || !payload || payload.length === 0) return null;
  const entry = payload[0];
  const data = (entry.payload || {}) as {
    subscriptions?: number;
    inrRevenue?: number;
    usdRevenue?: number;
    name?: string;
  };
  const subs = Number(data.subscriptions || 0);
  const inr = Number(data.inrRevenue || 0);
  const usd = Number(data.usdRevenue || 0);
  const combined = Number(entry.value || 0);
  const rawName = (data.name as string) || entry.name;
  return (
    <div className="bg-background border border-border rounded-lg shadow-lg p-3">
      <p className="font-medium text-foreground">{rawName}</p>
      <p
        className="text-sm"
        style={{ color: entry.color }}
      >{`Revenue: ₹${combined.toFixed(2)} (${subs})`}</p>
      <p className="text-xs text-muted-foreground">{`INR: ₹${inr.toFixed(
        2
      )} USD: $${usd.toFixed(2)}`}</p>
    </div>
  );
};

export const OverviewTab: React.FC<OverviewTabProps> = ({ dateRange }) => {
  // Fetch data for each time period (memoized to keep query keys stable)
  const todayRange = useMemo(() => getTodaySoFar(), []);
  const yesterdayRange = useMemo(() => getYesterday(), []);
  const thisMonthRange = useMemo(() => getThisMonth(), []);
  const lastMonthRange = useMemo(() => getLastMonth(), []);
  const dayBeforeYesterdayRange = useMemo(() => getDayBeforeYesterday(), []);
  const monthBeforeLastRange = useMemo(() => getMonthBeforeLast(), []);

  const { data: todayData, isLoading: todayLoading } = useQuery({
    queryKey: [
      "todayRevenue",
      todayRange.from.toISOString(),
      todayRange.to.toISOString(),
    ],
    queryFn: () => fetchRevenueAnalytics(todayRange),
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  const { data: yesterdayData, isLoading: yesterdayLoading } = useQuery({
    queryKey: [
      "yesterdayRevenue",
      yesterdayRange.from.toISOString(),
      yesterdayRange.to.toISOString(),
    ],
    queryFn: () => fetchRevenueAnalytics(yesterdayRange),
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  const { data: thisMonthData, isLoading: thisMonthLoading } = useQuery({
    queryKey: [
      "thisMonthRevenue",
      thisMonthRange.from.toISOString(),
      thisMonthRange.to.toISOString(),
    ],
    queryFn: () => fetchRevenueAnalytics(thisMonthRange),
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  const { data: lastMonthData, isLoading: lastMonthLoading } = useQuery({
    queryKey: [
      "lastMonthRevenue",
      lastMonthRange.from.toISOString(),
      lastMonthRange.to.toISOString(),
    ],
    queryFn: () => fetchRevenueAnalytics(lastMonthRange),
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  // Comparison datasets
  const { data: dayBeforeYesterdayData } = useQuery({
    queryKey: [
      "dayBeforeYesterdayRevenue",
      dayBeforeYesterdayRange.from.toISOString(),
      dayBeforeYesterdayRange.to.toISOString(),
    ],
    queryFn: () => fetchRevenueAnalytics(dayBeforeYesterdayRange),
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  const { data: monthBeforeLastData } = useQuery({
    queryKey: [
      "monthBeforeLastRevenue",
      monthBeforeLastRange.from.toISOString(),
      monthBeforeLastRange.to.toISOString(),
    ],
    queryFn: () => fetchRevenueAnalytics(monthBeforeLastRange),
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  const {
    data: analytics,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: [
      "overviewAnalytics",
      dateRange?.from?.toISOString?.() || "all-time",
      dateRange?.to?.toISOString?.() || "all-time",
    ],
    queryFn: () => fetchOverviewAnalytics(dateRange),
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  // Dedicated monthly growth trends with churn = cancelled + expired
  const { data: monthlyGrowthTrends } = useQuery({
    queryKey: ["monthlyGrowthTrends"],
    queryFn: () => fetchMonthlyGrowthTrends(),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  // Avoid hooks after early returns; compute constants safely here
  const planPerfColors = COLORS;

  const isLoading =
    loading ||
    todayLoading ||
    yesterdayLoading ||
    thisMonthLoading ||
    lastMonthLoading;

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
              <div className="h-4 w-4 bg-gray-300 dark:bg-gray-600 rounded"></div>
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load overview analytics. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  if (!analytics) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          No data available for the selected date range.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Note: Overview shows time-based metrics */}
      {/* <p className="text-sm text-muted-foreground">
        Overview shows time-based revenue metrics and all-time business
        performance
      </p> */}

      {/* Time-based Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Today So Far"
          value={
            <div className="flex items-center gap-3">
              <span className="font-semibold">{`₹${
                todayData?.inrRevenue?.toFixed(2) || "0.00"
              }`}</span>
              <span className="text-sm text-blue-600 dark:text-blue-400">{`$${
                todayData?.usdRevenue?.toFixed(2) || "0.00"
              }`}</span>
              <span className="text-xs text-muted-foreground">{`(${
                todayData?.subscribersCount || 0
              })`}</span>
            </div>
          }
          iconTooltip={(() => {
            const inr = Number(todayData?.inrRevenue || 0);
            const usd = Number(todayData?.usdRevenue || 0);
            const rate = 85;
            const combinedInInr = inr + usd * rate;
            const combinedInUsd = usd + inr / rate;
            return (
              <div>
                <div>{`INR: ₹${inr.toFixed(2)}`}</div>
                <div>{`USD: $${usd.toFixed(2)}`}</div>
                <div className="mt-1 opacity-80">{`Combined ≈ ₹${combinedInInr.toFixed(
                  2
                )} / $${combinedInUsd.toFixed(2)}`}</div>
              </div>
            );
          })()}
          icon={<Clock className="h-4 w-4" />}
          trend={{
            value: (() => {
              const today = todayData?.actualRevenue || 0;
              const y = yesterdayData?.actualRevenue || 0;
              if (!y) return 0;
              return Number((((today - y) / y) * 100).toFixed(1));
            })(),
            isPositive:
              (todayData?.actualRevenue || 0) -
                (yesterdayData?.actualRevenue || 0) >=
              0,
            period: "yesterday",
          }}
        />
        <StatsCard
          title="Yesterday"
          value={
            <div className="flex items-center gap-3">
              <span className="font-semibold">{`₹${
                yesterdayData?.inrRevenue?.toFixed(2) || "0.00"
              }`}</span>
              <span className="text-sm text-blue-600 dark:text-blue-400">{`$${
                yesterdayData?.usdRevenue?.toFixed(2) || "0.00"
              }`}</span>
              <span className="text-xs text-muted-foreground">{`(${
                yesterdayData?.subscribersCount || 0
              })`}</span>
            </div>
          }
          iconTooltip={(() => {
            const inr = Number(yesterdayData?.inrRevenue || 0);
            const usd = Number(yesterdayData?.usdRevenue || 0);
            const rate = 85;
            const combinedInInr = inr + usd * rate;
            const combinedInUsd = usd + inr / rate;
            return (
              <div>
                <div>{`INR: ₹${inr.toFixed(2)}`}</div>
                <div>{`USD: $${usd.toFixed(2)}`}</div>
                <div className="mt-1 opacity-80">{`Combined ≈ ₹${combinedInInr.toFixed(
                  2
                )} / $${combinedInUsd.toFixed(2)}`}</div>
              </div>
            );
          })()}
          icon={<Calendar className="h-4 w-4" />}
          trend={{
            value: (() => {
              const y = yesterdayData?.actualRevenue || 0;
              const dby = dayBeforeYesterdayData?.actualRevenue || 0;
              if (!dby) return 0;
              return Number((((y - dby) / dby) * 100).toFixed(1));
            })(),
            isPositive:
              (yesterdayData?.actualRevenue || 0) -
                (dayBeforeYesterdayData?.actualRevenue || 0) >=
              0,
            period: "prev day",
          }}
        />
        <StatsCard
          title="This Month"
          value={
            <div className="flex items-center gap-3">
              <span className="font-semibold">{`₹${
                thisMonthData?.inrRevenue?.toFixed(2) || "0.00"
              }`}</span>
              <span className="text-sm text-blue-600 dark:text-blue-400">{`$${
                thisMonthData?.usdRevenue?.toFixed(2) || "0.00"
              }`}</span>
              <span className="text-xs text-muted-foreground">{`(${
                thisMonthData?.subscribersCount || 0
              })`}</span>
            </div>
          }
          iconTooltip={(() => {
            const inr = Number(thisMonthData?.inrRevenue || 0);
            const usd = Number(thisMonthData?.usdRevenue || 0);
            const rate = 85;
            const combinedInInr = inr + usd * rate;
            const combinedInUsd = usd + inr / rate;
            return (
              <div>
                <div>{`INR: ₹${inr.toFixed(2)}`}</div>
                <div>{`USD: $${usd.toFixed(2)}`}</div>
                <div className="mt-1 opacity-80">{`Combined ≈ ₹${combinedInInr.toFixed(
                  2
                )} / $${combinedInUsd.toFixed(2)}`}</div>
              </div>
            );
          })()}
          icon={<TrendingUp className="h-4 w-4" />}
          trend={{
            value: (() => {
              const tm = thisMonthData?.actualRevenue || 0;
              const lm = lastMonthData?.actualRevenue || 0;
              if (!lm) return 0;
              return Number((((tm - lm) / lm) * 100).toFixed(1));
            })(),
            isPositive:
              (thisMonthData?.actualRevenue || 0) -
                (lastMonthData?.actualRevenue || 0) >=
              0,
            period: "last month",
          }}
        />
        <StatsCard
          title="Last Month"
          value={
            <div className="flex items-center gap-3">
              <span className="font-semibold">{`₹${
                lastMonthData?.inrRevenue?.toFixed(2) || "0.00"
              }`}</span>
              <span className="text-sm text-blue-600 dark:text-blue-400">{`$${
                lastMonthData?.usdRevenue?.toFixed(2) || "0.00"
              }`}</span>
              <span className="text-xs text-muted-foreground">{`(${
                lastMonthData?.subscribersCount || 0
              })`}</span>
            </div>
          }
          iconTooltip={(() => {
            const inr = Number(lastMonthData?.inrRevenue || 0);
            const usd = Number(lastMonthData?.usdRevenue || 0);
            const rate = 85;
            const combinedInInr = inr + usd * rate;
            const combinedInUsd = usd + inr / rate;
            return (
              <div>
                <div>{`INR: ₹${inr.toFixed(2)}`}</div>
                <div>{`USD: $${usd.toFixed(2)}`}</div>
                <div className="mt-1 opacity-80">{`Combined ≈ ₹${combinedInInr.toFixed(
                  2
                )} / $${combinedInUsd.toFixed(2)}`}</div>
              </div>
            );
          })()}
          icon={<DollarSign className="h-4 w-4" />}
          trend={{
            value: (() => {
              const lm = lastMonthData?.actualRevenue || 0;
              const mbl = monthBeforeLastData?.actualRevenue || 0;
              if (!mbl) return 0;
              return Number((((lm - mbl) / mbl) * 100).toFixed(1));
            })(),
            isPositive:
              (lastMonthData?.actualRevenue || 0) -
                (monthBeforeLastData?.actualRevenue || 0) >=
              0,
            period: "prev month",
          }}
        />
      </div>

      {/* Additional Business Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Active Subscriptions"
          value={analytics.activeSubscriptions}
          icon={<Users className="h-4 w-4" />}
          footer={
            <span className="text-xs text-muted-foreground">
              Expired: {analytics.expiredSubscriptionsCount ?? 0}
            </span>
          }
        />
        <StatsCard
          title="Active Subscription Plans"
          value={Math.max(
            0,
            (analytics.subscriptionPlansCount || 0) -
              (analytics.subscriptionPlansInactiveCount || 0)
          )}
          icon={<Target className="h-4 w-4" />}
          footer={
            <span className="text-xs text-muted-foreground">
              Inactive: {analytics.subscriptionPlansInactiveCount ?? 0}
            </span>
          }
        />
        <StatsCard
          title="Active Promotions"
          value={analytics.promotionsActiveCount ?? 0}
          icon={<Target className="h-4 w-4" />}
          footer={
            <span className="text-xs text-muted-foreground">
              Inactive: {analytics.promotionsInactiveCount ?? 0}
            </span>
          }
        />
        <StatsCard
          title="Lifetime Revenue"
          value={`₹${Number(
            (analytics.lifetimeGrossRevenueInr ??
              analytics.lifetimeGrossRevenue) ||
              0
          ).toFixed(2)}`}
          icon={<DollarSign className="h-4 w-4" />}
          iconTooltip={(() => {
            const inr = Number(analytics.lifetimeGrossRevenueInr ?? 0);
            const usd = Number(analytics.lifetimeGrossRevenueUsd ?? 0);
            const rate = 85;
            const combinedInInr = inr + usd * rate;
            const combinedInUsd = usd + inr / rate;
            return (
              <div>
                <div>{`INR: ₹${inr.toFixed(2)}`}</div>
                <div>{`USD: $${usd.toFixed(2)}`}</div>
                <div className="mt-1 opacity-80">{`Combined ≈ ₹${combinedInInr.toFixed(
                  2
                )} / $${combinedInUsd.toFixed(2)}`}</div>
              </div>
            );
          })()}
          footer={
            <div className="mt-1 text-sm font-semibold text-blue-600 dark:text-blue-400">
              {`$${Number(analytics.lifetimeGrossRevenueUsd ?? 0).toFixed(2)}`}
            </div>
          }
        />
      </div>

      {/* Business Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Daily Trends - full row */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle>Daily Revenue & Subscription Trends</CardTitle>
            <CardDescription>
              Daily revenue split by INR/USD and subscription growth (last 30
              days)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer
              width="100%"
              height={350}
              className="[&_.recharts-bar]:hover:brightness-110 [&_.recharts-bar]:hover:cursor-pointer [&_.recharts-bar]:transition-all [&_.recharts-bar]:duration-200"
            >
              <ComposedChart
                data={
                  analytics.dailyTrends && analytics.dailyTrends.length > 0
                    ? analytics.dailyTrends
                    : analytics.historicalTrends
                }
              >
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis
                  dataKey={
                    analytics.dailyTrends && analytics.dailyTrends.length > 0
                      ? "day"
                      : "month"
                  }
                  className="text-muted-foreground"
                />
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  className="text-muted-foreground"
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  className="text-muted-foreground"
                />
                <RechartsTooltip
                  content={<CustomTooltip />}
                  formatter={(value, name) => [
                    typeof name === "string"
                      ? name.includes("MRR")
                        ? `₹${value}`
                        : name.includes("INR")
                        ? `₹${value}`
                        : name.includes("USD")
                        ? `$${value}`
                        : value
                      : value,
                    name,
                  ]}
                />
                {analytics.dailyTrends && analytics.dailyTrends.length > 0 ? (
                  <>
                    <Bar
                      yAxisId="left"
                      dataKey="inrRevenue"
                      fill="#3b82f6"
                      name="INR Revenue"
                      stackId="revenue"
                      className="hover:brightness-110 transition-all duration-200 cursor-pointer"
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="usdRevenue"
                      fill="#f59e0b"
                      name="USD Revenue"
                      stackId="revenue"
                      className="hover:brightness-110 transition-all duration-200 cursor-pointer"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="totalSubscriptions"
                      stroke="#10b981"
                      strokeWidth={2}
                      name="Total Subscriptions"
                      className="hover:brightness-110"
                    />
                  </>
                ) : (
                  <>
                    <Bar
                      yAxisId="left"
                      dataKey="totalMRR"
                      fill="#3b82f6"
                      name="Total MRR"
                      className="hover:brightness-110 transition-all duration-200 cursor-pointer"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="totalSubscriptions"
                      stroke="#10b981"
                      strokeWidth={2}
                      name="Total Subscriptions"
                      className="hover:brightness-110"
                    />
                  </>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Monthly Trends */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Monthly Revenue & Subscription Trends</CardTitle>
            <CardDescription>
              Monthly MRR and subscription growth (last 12 months)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer
              width="100%"
              height={350}
              className="[&_.recharts-bar]:hover:brightness-110 [&_.recharts-bar]:hover:cursor-pointer [&_.recharts-bar]:transition-all [&_.recharts-bar]:duration-200"
            >
              <ComposedChart data={analytics.historicalTrends}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="month" className="text-muted-foreground" />
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  className="text-muted-foreground"
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  className="text-muted-foreground"
                />
                <RechartsTooltip
                  content={<CustomTooltip />}
                  formatter={(value, name) => [
                    typeof name === "string"
                      ? name.includes("MRR")
                        ? `₹${value}`
                        : name.includes("INR")
                        ? `₹${value}`
                        : name.includes("USD")
                        ? `$${value}`
                        : value
                      : value,
                    name,
                  ]}
                />
                {/* Show stacked INR/USD bars if present, else fallback to MRR */}
                {typeof analytics.historicalTrends?.[0]?.inrRevenue ===
                  "number" ||
                typeof analytics.historicalTrends?.[0]?.usdRevenue ===
                  "number" ? (
                  <>
                    <Bar
                      yAxisId="left"
                      dataKey="inrRevenue"
                      fill="#3b82f6"
                      name="INR Revenue"
                      stackId="revenue-monthly"
                      className="hover:brightness-110 transition-all duration-200 cursor-pointer"
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="usdRevenue"
                      fill="#f59e0b"
                      name="USD Revenue"
                      stackId="revenue-monthly"
                      className="hover:brightness-110 transition-all duration-200 cursor-pointer"
                    />
                  </>
                ) : (
                  <Bar
                    yAxisId="left"
                    dataKey="totalMRR"
                    fill="#3b82f6"
                    name="Total MRR"
                    className="hover:brightness-110 transition-all duration-200 cursor-pointer"
                  />
                )}
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="totalSubscriptions"
                  stroke="#10b981"
                  strokeWidth={2}
                  name="Total Subscriptions"
                  className="hover:brightness-110"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Plan Performance */}
        <Card>
          <CardHeader>
            <CardTitle>Plan Performance</CardTitle>
            <CardDescription>
              Revenue distribution by subscription plans
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer
              width="100%"
              height={350}
              className="[&_.recharts-pie-sector]:hover:brightness-110 [&_.recharts-pie-sector]:hover:cursor-pointer [&_.recharts-pie-sector]:transition-all [&_.recharts-pie-sector]:duration-200"
            >
              <PieChart>
                <Pie
                  data={analytics.planPerformance}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="revenue"
                  label={({
                    name,
                    value,
                    payload,
                  }: {
                    name: string;
                    value: number;
                    payload: { subscriptions?: number };
                  }) => {
                    const shortName =
                      typeof name === "string"
                        ? name.split("–")[0].trim()
                        : name;
                    const subs = payload?.subscriptions ?? 0;
                    return `${shortName}: ₹${value} (${subs})`;
                  }}
                  className="hover:brightness-110"
                >
                  {analytics.planPerformance.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={planPerfColors[index % planPerfColors.length]}
                      className="hover:brightness-110 transition-all duration-200 cursor-pointer"
                    />
                  ))}
                </Pie>
                <RechartsTooltip content={<PlanPieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Plan Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle>Subscription Plan Analytics</CardTitle>
          <CardDescription>
            Detailed performance metrics for each subscription plan
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {analytics.planPerformance.map((plan, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors duration-200"
              >
                <div className="flex items-center space-x-4">
                  <div
                    className={`w-4 h-4 rounded-full`}
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  ></div>
                  <div>
                    <h4 className="font-medium">{plan.name}</h4>
                    <p className="text-sm text-muted-foreground">
                      {plan.subscriptions} subscribers
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium">₹{plan.revenue.toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground">
                    INR: ₹
                    {Number(
                      (plan as { inrRevenue?: number }).inrRevenue ?? 0
                    ).toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    USD: $
                    {Number(
                      (plan as { usdRevenue?: number }).usdRevenue ?? 0
                    ).toFixed(2)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Avg: ₹{plan.avgPrice.toFixed(2)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Monthly Trends Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly Growth Analysis</CardTitle>
          <CardDescription>
            New vs churned subscriptions and revenue trends
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer
            width="100%"
            height={300}
            className="[&_.recharts-bar]:hover:brightness-110 [&_.recharts-bar]:hover:cursor-pointer [&_.recharts-bar]:transition-all [&_.recharts-bar]:duration-200"
          >
            <RechartsBarChart data={monthlyGrowthTrends || []}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="month" className="text-muted-foreground" />
              <YAxis className="text-muted-foreground" />
              <RechartsTooltip content={<CustomTooltip />} />
              <RechartsTooltip content={<CustomTooltip />} />
              <Bar
                dataKey="newSubscriptions"
                stackId="a"
                fill="#10b981"
                name="New Subscriptions"
                className="hover:brightness-110 transition-all duration-200 cursor-pointer"
              />
              <Bar
                dataKey="churnedSubscriptions"
                stackId="a"
                fill="#ef4444"
                name="Churned Subscriptions"
                className="hover:brightness-110 transition-all duration-200 cursor-pointer"
              />
            </RechartsBarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};
